import { isString } from 'es-toolkit';
import { type RumErrorEvent, type RumEvent } from '@datadog/browser-rum';

import { rumErrorFilterRules } from './rules';

const botUserAgentPatterns = [
  /compatible;\s*AcmeInc\/1\.0/i,
  /Bytespider/i,
  /HeadlessChrome/i,
  /bingbot/i,
  /crawler/i,
  /spider/i,
  /meta-externalads/i,
  /Lightpanda/i,
] as const;

export type RumErrorFilterRule = {
  name: string;
  source?: RumErrorEvent['error']['source'];
  sourceType?: RumErrorEvent['error']['source_type'];
  errorType?: RumErrorEvent['error']['type'];
  file?: string;
  fileIncludes?: string[];
  fileRegex?: RegExp;
  handlingStackIncludes?: string[];
  message?: string;
  messageIncludes?: string[];
  messageRegex?: RegExp;
  stackIncludes?: string[];
  userAgentIncludes?: string[];
  userAgentRegex?: RegExp;
};

type RumEventWithUserAgent = RumEvent & {
  context?: {
    session?: {
      useragent?: unknown;
      userAgent?: unknown;
    };
    useragent?: unknown;
    userAgent?: unknown;
  };
  session?: {
    useragent?: unknown;
    userAgent?: unknown;
  };
};

const matchesRumErrorFilterRule = (event: RumErrorEvent, rule: RumErrorFilterRule) => {
  const errorFile = event.error.file;
  const userAgent = getRumEventUserAgent(event);

  if (rule.source && event.error.source !== rule.source) {
    return false;
  }

  if (rule.sourceType && event.error.source_type !== rule.sourceType) {
    return false;
  }

  if (rule.errorType && event.error.type !== rule.errorType) {
    return false;
  }

  if (rule.file && errorFile !== rule.file) {
    return false;
  }

  if (rule.fileIncludes && (!isString(errorFile) || !rule.fileIncludes.every((filePart) => errorFile.includes(filePart)))) {
    return false;
  }

  if (rule.fileRegex && (!isString(errorFile) || !rule.fileRegex.test(errorFile))) {
    return false;
  }

  if (
    rule.handlingStackIncludes &&
    (!isString(event.error.handling_stack) ||
      !rule.handlingStackIncludes.every((stackPart) => event.error.handling_stack?.includes(stackPart)))
  ) {
    return false;
  }

  if (rule.message && event.error.message !== rule.message) {
    return false;
  }

  if (rule.messageIncludes && !rule.messageIncludes.every((messagePart) => event.error.message.includes(messagePart))) {
    return false;
  }

  if (rule.messageRegex && !rule.messageRegex.test(event.error.message)) {
    return false;
  }

  if (
    rule.stackIncludes &&
    (!isString(event.error.stack) || !rule.stackIncludes.every((stackPart) => event.error.stack?.includes(stackPart)))
  ) {
    return false;
  }

  if (rule.userAgentIncludes && (!isString(userAgent) || !rule.userAgentIncludes.every((value) => userAgent.includes(value)))) {
    return false;
  }

  if (rule.userAgentRegex && (!isString(userAgent) || !rule.userAgentRegex.test(userAgent))) {
    return false;
  }

  return true;
};

const getCurrentUserAgent = () => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return navigator.userAgent;
};

const getRumEventUserAgent = (event: RumEvent) => {
  const eventWithUserAgent = event as RumEventWithUserAgent;
  const userAgent =
    eventWithUserAgent.session?.useragent ??
    eventWithUserAgent.session?.userAgent ??
    eventWithUserAgent.context?.session?.useragent ??
    eventWithUserAgent.context?.session?.userAgent ??
    eventWithUserAgent.context?.useragent ??
    eventWithUserAgent.context?.userAgent;

  return isString(userAgent) ? userAgent : getCurrentUserAgent();
};

const isKnownBotUserAgent = (userAgent: string | undefined) => {
  if (!userAgent) return false;

  return botUserAgentPatterns.some((pattern) => pattern.test(userAgent));
};

const shouldFilterRumEvent = (event: RumEvent) => {
  if (isKnownBotUserAgent(getRumEventUserAgent(event))) {
    return true;
  }

  if (event.type !== 'error') {
    return false;
  }

  return rumErrorFilterRules.some((rule) => matchesRumErrorFilterRule(event, rule));
};

export const shouldSendRumEvent = (event: RumEvent) => !shouldFilterRumEvent(event);
